import React, { useContext } from 'react';
import { assertNotNull } from '../../../../../helpers/nullHelpers';
import { TextInputField } from '../../../../../shared/form/inputs/TextInputField';
import { Td, Tr, TBody, THead } from '../../../../../shared/Table';
import {
  FullHeightTh,
  FullHeightThBorderLeft,
  FullHeightThBorderRight,
  GroupedTh,
  GroupedThBorderLeft,
  GroupedThBorderRight,
  GroupingTh,
  TdBorderLeft,
  TdBorderRight,
} from '../../../../../shared/SplitTable';
import { DesignFormDefaultsContext } from '../../../../designs/DesignFormDefaultsContext';
import { QuoteOrDesignFormContext } from '../../QuoteOrDesignFormContext';
import { RoomFormModel } from '../../rooms/roomFormModel';
import { UnitFormModel } from '../unitFormModel';
import { RoomsHeatLossInputField } from './RoomsHeatLossInputField';
import { RoomsHeatLossTableColumnHeaderContextMenu } from './RoomsHeatLossTableColumnHeaderContextMenu';

type Props = {
  unit: UnitFormModel;
  filteredManifoldId: string | null;
  fillDownColumn: (columnFieldName: string) => () => void;
  roomHeatLossDetailsFieldName: (manifoldIndex: number, roomIndex: number, fieldName: string) => string;
};

export const RoomsRoofTable = ({ unit, filteredManifoldId, fillDownColumn, roomHeatLossDetailsFieldName }: Props) => {
  const formContext = assertNotNull(useContext(QuoteOrDesignFormContext));
  const { setFieldValue } = formContext.formProps;
  const roofElements = assertNotNull(unit.heatLossDetails).roofElements;
  const { unitRoofElements, setUnitRoofElements } = assertNotNull(useContext(DesignFormDefaultsContext));

  const roomRoofHeatLossDetailsFieldName = (
    manifoldIndex: number,
    roomIndex: number,
    roofElementIndex: number,
    fieldName: string,
  ) => roomHeatLossDetailsFieldName(manifoldIndex, roomIndex, `roofs.${roofElementIndex}.${fieldName}`);

  const onChangeCeilingHeight = (manifoldIndex: number, room: RoomFormModel, roomIndex: number, newValue: string) => {
    const newCeilingHeight = Number(newValue);
    const oldCeilingHeight = Number(room.heatLossDetails?.ceilingHeightInMetres);

    if (isNaN(newCeilingHeight) || isNaN(oldCeilingHeight)) {
      return;
    }

    roofElements.forEach((_, roofElementIndex) => {
      const maximumVaultHeightInMetres = Number(
        room.heatLossDetails?.roofs[roofElementIndex].maximumVaultHeightInMetres,
      );

      if (isNaN(maximumVaultHeightInMetres)) {
        return;
      }

      if (newCeilingHeight >= maximumVaultHeightInMetres || maximumVaultHeightInMetres === oldCeilingHeight) {
        setFieldValue(
          roomRoofHeatLossDetailsFieldName(manifoldIndex, roomIndex, roofElementIndex, 'maximumVaultHeightInMetres'),
          newCeilingHeight.toString(),
        );
      }
    });
  };

  const onTemperatureAboveChange = (
    manifoldIndex: number,
    roomIndex: number,
    roofElementIndex: number,
    newValue: string,
  ) => {
    if (unitRoofElements) {
      const newUnitRoofElements = [...unitRoofElements];
      newUnitRoofElements[roofElementIndex].manifoldRoomValues[manifoldIndex][roomIndex] = newValue;
      setUnitRoofElements(newUnitRoofElements);
    }
  };

  return (
    <>
      <THead>
        <Tr>
          <FullHeightThBorderLeft rowSpan={2}>
            <RoomsHeatLossTableColumnHeaderContextMenu fillDownColumn={fillDownColumn('numberOfAirChangesPerHour')}>
              Air Changes Per Hour
            </RoomsHeatLossTableColumnHeaderContextMenu>
          </FullHeightThBorderLeft>
          <FullHeightTh rowSpan={2}>
            <RoomsHeatLossTableColumnHeaderContextMenu
              fillDownColumn={fillDownColumn('mechanicalVentilationHeatRecoveryEfficiencyPercentage')}
            >
              MVHR Efficiency (%)
            </RoomsHeatLossTableColumnHeaderContextMenu>
          </FullHeightTh>
          <FullHeightThBorderRight rowSpan={2}>
            <RoomsHeatLossTableColumnHeaderContextMenu fillDownColumn={fillDownColumn('ceilingHeightInMetres')}>
              Ceiling Height (m)
            </RoomsHeatLossTableColumnHeaderContextMenu>
          </FullHeightThBorderRight>
          {roofElements.map((roofElement, index) => (
            <GroupingTh key={index} colSpan={5}>
              {roofElement.name || 'Unnamed Roof Element'}
            </GroupingTh>
          ))}
        </Tr>
        <Tr>
          {roofElements.map((_, index) => (
            <React.Fragment key={index}>
              <GroupedThBorderLeft>
                <RoomsHeatLossTableColumnHeaderContextMenu
                  fillDownColumn={fillDownColumn(`roofs.${index}.maximumVaultHeightInMetres`)}
                >
                  Max Vault Height (m)
                </RoomsHeatLossTableColumnHeaderContextMenu>
              </GroupedThBorderLeft>
              <GroupedTh>
                <RoomsHeatLossTableColumnHeaderContextMenu
                  fillDownColumn={fillDownColumn(`roofs.${index}.roomAreaCoveredPercentage`)}
                >
                  Room Area Covered (%)
                </RoomsHeatLossTableColumnHeaderContextMenu>
              </GroupedTh>
              <GroupedTh>
                <RoomsHeatLossTableColumnHeaderContextMenu
                  fillDownColumn={fillDownColumn(`roofs.${index}.additionalRoomVolumeInMetresCubed`)}
                >
                  <>
                    Additional Room Vol. (m<sup>3</sup>)
                  </>
                </RoomsHeatLossTableColumnHeaderContextMenu>
              </GroupedTh>
              <GroupedTh>
                <RoomsHeatLossTableColumnHeaderContextMenu
                  fillDownColumn={fillDownColumn(`roofs.${index}.skylightAreaInMetresSquared`)}
                >
                  Skylight Area (m<sup>2</sup>)
                </RoomsHeatLossTableColumnHeaderContextMenu>
              </GroupedTh>
              <GroupedThBorderRight>
                <RoomsHeatLossTableColumnHeaderContextMenu
                  fillDownColumn={fillDownColumn(`roofs.${index}.temperatureAboveInDegreesCelsius`)}
                >
                  Temp. Above (°C)
                </RoomsHeatLossTableColumnHeaderContextMenu>
              </GroupedThBorderRight>
            </React.Fragment>
          ))}
        </Tr>
      </THead>
      <TBody>
        {unit.manifolds
          .map((manifold, manifoldIndex) => ({ manifold, manifoldIndex }))
          .filter(({ manifold }) => filteredManifoldId === null || manifold.id === filteredManifoldId)
          .map(({ manifold, manifoldIndex }) =>
            manifold.rooms == null
              ? null
              : manifold.rooms.map((room, roomIndex) => (
                  <Tr key={`${manifoldIndex}-${roomIndex}`}>
                    <TdBorderLeft>
                      <TextInputField
                        name={roomHeatLossDetailsFieldName(manifoldIndex, roomIndex, 'numberOfAirChangesPerHour')}
                        label={null}
                      />
                    </TdBorderLeft>
                    <Td>
                      <TextInputField
                        name={roomHeatLossDetailsFieldName(
                          manifoldIndex,
                          roomIndex,
                          'mechanicalVentilationHeatRecoveryEfficiencyPercentage',
                        )}
                        label={null}
                      />
                    </Td>
                    <TdBorderRight>
                      <TextInputField
                        name={roomHeatLossDetailsFieldName(manifoldIndex, roomIndex, 'ceilingHeightInMetres')}
                        label={null}
                        onChange={newValue => onChangeCeilingHeight(manifoldIndex, room, roomIndex, newValue)}
                      />
                    </TdBorderRight>
                    {roofElements.map((_, roofElementIndex) => (
                      <React.Fragment key={roofElementIndex}>
                        <TdBorderLeft>
                          <RoomsHeatLossInputField
                            name={roomRoofHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              roofElementIndex,
                              'maximumVaultHeightInMetres',
                            )}
                            referenceFieldName={roomRoofHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              roofElementIndex,
                              'roomAreaCoveredPercentage',
                            )}
                            label={null}
                          />
                        </TdBorderLeft>
                        <Td>
                          <TextInputField
                            name={roomRoofHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              roofElementIndex,
                              'roomAreaCoveredPercentage',
                            )}
                            label={null}
                          />
                        </Td>
                        <Td>
                          <RoomsHeatLossInputField
                            name={roomRoofHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              roofElementIndex,
                              'additionalRoomVolumeInMetresCubed',
                            )}
                            referenceFieldName={roomRoofHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              roofElementIndex,
                              'roomAreaCoveredPercentage',
                            )}
                            label={null}
                          />
                        </Td>
                        <Td>
                          <RoomsHeatLossInputField
                            name={roomRoofHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              roofElementIndex,
                              'skylightAreaInMetresSquared',
                            )}
                            label={null}
                            referenceFieldName={roomRoofHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              roofElementIndex,
                              'roomAreaCoveredPercentage',
                            )}
                          />
                        </Td>
                        <TdBorderRight>
                          <RoomsHeatLossInputField
                            name={roomRoofHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              roofElementIndex,
                              'temperatureAboveInDegreesCelsius',
                            )}
                            referenceFieldName={roomRoofHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              roofElementIndex,
                              'roomAreaCoveredPercentage',
                            )}
                            label={null}
                            onChange={newValue =>
                              onTemperatureAboveChange(manifoldIndex, roomIndex, roofElementIndex, newValue)
                            }
                          />
                        </TdBorderRight>
                      </React.Fragment>
                    ))}
                  </Tr>
                )),
          )}
      </TBody>
    </>
  );
};
